import { useEffect, useRef } from 'react';
import type { HexTileMapKey } from '@/game/hexmap';
import { getHexTileMapKey } from '@/game/hexmap';
import {
  buildingLevel,
  buildingMaxHp,
  buildingStrength,
  canPlace,
  cityBonuses,
  covers,
  fromMilli,
  gatherRate,
  gatherResource,
  isCity,
  outsideTerritory,
  tileResources,
  workerSlots,
} from '@/game/sim';
import { assignedWorkers, farmWorkers } from '@/game/view/activity';
import { constructionStatus, formatDuration } from '@/game/view/construction';
import type { Frame } from '@/game/view/frame';
import { roomToGrow } from '@/game/view/growth';
import { type CityIntel, cityIntel } from '@/game/view/lastSeen';
import { rivalSprawlCity } from '@/game/view/sprawl';
import { tickWorkers } from '@/game/view/tickCache';
import { TILE_NAMES } from '@/game/view/workerIntent';
import { labelPosition } from '@/game/view/workTarget';
import { useSimFrame } from './app/simFrame';
import { type UiStore, useUi } from './ui/store';

interface TileInfoPanelProps {
  hoveredKey: HexTileMapKey | null;
  frame: Frame;
}

/**
 * The tile info slot: the hovered tile, else the one clicked to inspect
 * (`hover: false`: only the inspected one, for a layout whose hover info
 * follows the cursor instead, `CursorTileTip`). A
 * visible rival sprawl tile shows its city (a click there aims at the city);
 * a tile with a unit on it (own, or a rival in sight) keeps its own info.
 * The only part of the HUD that re-renders as the cursor moves.
 */
export function HoveredTileInfo({
  ui,
  inspectKey,
  hover = true,
}: {
  ui: UiStore;
  inspectKey: HexTileMapKey | null;
  hover?: boolean;
}) {
  const frame = useSimFrame();
  const { view: state, playerId, fog } = frame;
  const hoveredKey = useUi(ui, (s) => (hover ? s.hoveredKey : null));
  const panelKey = hoveredKey ?? inspectKey;
  const panelHasUnit =
    panelKey !== null &&
    Object.values(state.units).some(
      (u) =>
        !u.inside &&
        getHexTileMapKey(u.pos) === panelKey &&
        // Vision including revealed attackers, for whether a rival unit is seen.
        (u.owner === playerId || fog.visibility(panelKey) === 'visible')
    );
  const sprawlCity =
    panelKey && !panelHasUnit
      ? rivalSprawlCity(state, frame.ctx, playerId, fog.sight, panelKey)
      : null;
  return (
    <TileInfoPanel
      hoveredKey={sprawlCity ? getHexTileMapKey(sprawlCity.pos) : panelKey}
      frame={frame}
    />
  );
}

function Bar({ fraction, className }: { fraction: number; className: string }) {
  return (
    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-fill">
      <div
        className={`h-full rounded-full ${className}`}
        style={{ width: `${Math.max(0, Math.min(1, fraction)) * 100}%` }}
      />
    </div>
  );
}

/** The intel line under a rival city's header, which already shows its level and hp. */
export function intelNote(intel: CityIntel): string {
  const soldiers = `${intel.defenders} ${intel.defenders === 1 ? 'soldier' : 'soldiers'} in sight`;
  return intel.seen === 'remembered' ? `As last seen · ${soldiers}` : soldiers;
}

/**
 * "Level 3 (last seen) · hp 220/380 (last seen) · 4 soldiers in sight": a
 * rival city's defence as the player knows it (`cityIntel`, fog-safe).
 */
export function intelText(intel: CityIntel): string {
  const seen = intel.seen === 'remembered' ? ' (last seen)' : '';
  const hp = `${Math.ceil(fromMilli(intel.hp))}/${Math.ceil(fromMilli(intel.maxHp))}`;
  const soldiers = `${intel.defenders} ${intel.defenders === 1 ? 'soldier' : 'soldiers'} in sight`;
  return `Level ${intel.level}${seen} · hp ${hp}${seen} · ${soldiers}`;
}

/** What's on the hovered tile: resources left, gathering rate, construction. */
export function TileInfoPanel({ hoveredKey, frame }: TileInfoPanelProps) {
  const { view: state, terrain, rules, playerId, ctx } = frame;
  if (!hoveredKey) return null;
  const tile = terrain.get(hoveredKey);
  if (!tile) return null;

  // By sight: the tile itself, not a revealed attacker on it.
  const visibility = frame.fog.sight(hoveredKey);
  if (visibility === 'unexplored') {
    return (
      <div className="panel w-60 p-3 text-xs">
        <p className="text-fg-muted">Unexplored</p>
      </div>
    );
  }

  const inSight = visibility === 'visible';
  // Out of sight: the tile as last seen (plus the player's own building), so
  // the panel never shows rival buildings or gathering hidden by fog.
  const known = frame.tileView(hoveredKey);
  const shownBuilding = known.building ?? undefined;
  // A rival building out of sight is shown as last seen, without live details.
  const remembered = !inSight && shownBuilding !== undefined && shownBuilding.owner !== playerId;
  const buildingDef = shownBuilding ? rules.buildings[shownBuilding.type] : undefined;
  const build =
    shownBuilding && shownBuilding.buildProgress < 1
      ? remembered
        ? { progress: shownBuilding.buildProgress, secondsLeft: null }
        : constructionStatus(state, rules, shownBuilding)
      : null;
  // Barracks bonuses and a last stand only for own buildings: a rival's
  // barracks could sit under fog, and a last stand tells how many cities they have.
  const own = shownBuilding?.owner === playerId;
  const bonuses = own ? cityBonuses(state, rules) : new Map();
  const statsOf =
    shownBuilding && !own ? { ...shownBuilding, lastStand: undefined } : shownBuilding;
  const maxHp = statsOf
    ? Math.ceil(fromMilli(Math.max(buildingMaxHp(state, rules, statsOf, bonuses), statsOf.hp)))
    : 0;
  const strength = statsOf ? buildingStrength(rules, statsOf, bonuses) : 0;
  // Own building outside its territory: idle (a rival's is never checked, so
  // fog can't leak their borders).
  const idleOutside =
    !!shownBuilding &&
    shownBuilding.owner === playerId &&
    outsideTerritory(state, rules, shownBuilding);
  const providesForever =
    shownBuilding && shownBuilding.buildProgress >= 1 && !idleOutside && buildingDef?.provides;

  const live = tileResources(state, ctx, tile.coordinates);
  // In whole units (the sim keeps thousandths).
  const wild = live && {
    ...live,
    initial: fromMilli(live.initial),
    remaining: fromMilli(known.remaining === null ? live.initial : Math.max(0, known.remaining)),
  };
  const resourceName = wild ? (rules.resources[wild.resourceId]?.name ?? wild.resourceId) : '';
  // Only gathering the player can see (their own units always give sight).
  // On a (grown) farm, workers on any of its tiles; for a rival's, only on
  // its tiles in sight.
  const ownBuilding = shownBuilding?.owner === playerId;
  const seenAt = (cell: { q: number; r: number }) =>
    ownBuilding || frame.fog.sight(getHexTileMapKey(cell)) === 'visible';
  const gatherers = inSight
    ? Object.values(state.units).filter(
        (u) =>
          (providesForever && shownBuilding
            ? covers(shownBuilding, u.pos) && seenAt(u.pos)
            : getHexTileMapKey(u.pos) === hoveredKey) && gatherResource(state, ctx, u) !== null
      )
    : [];
  const ratePerSecond = gatherers.reduce((sum, u) => sum + gatherRate(state, ctx, u), 0);
  // As its 👷 badge and the building panel count them: an own one's workers
  // inside or hauling for it, a rival's only those inside on its tiles in sight.
  const workers = shownBuilding
    ? ownBuilding
      ? assignedWorkers(state, rules, shownBuilding, tickWorkers(state, rules))
      : { ...farmWorkers(state, rules, shownBuilding, seenAt), hauling: 0 }
    : null;
  // Sites the player could place here (affordable or not), from what they know.
  const buildable = shownBuilding
    ? []
    : Object.keys(rules.buildings).filter((type) => {
        const check = canPlace(
          state,
          ctx,
          playerId,
          type,
          tile.coordinates,
          () => false // no building known here
        );
        return check.ok || check.reason === 'cost';
      });
  // A rival's tiles as far as the player knows (in sight or last seen as it).
  const viewer = frame.tileView;
  const extraTiles = (shownBuilding?.footprint ?? []).filter(
    (c) => ownBuilding || viewer(getHexTileMapKey(c)).building?.id === shownBuilding?.id
  ).length;
  const growth =
    shownBuilding && shownBuilding.owner === playerId && shownBuilding.buildProgress >= 1
      ? roomToGrow(state, ctx, shownBuilding)
      : null;
  // A rival city: its defence as the player knows it (level and hp as last
  // seen, soldiers in sight in its territory now).
  const intel =
    shownBuilding && !own && isCity(shownBuilding, rules)
      ? cityIntel(state, rules, frame.knownBuildings(), playerId, shownBuilding.id, frame.fog.sight)
      : null;

  return (
    <div className="panel max-h-full w-60 space-y-2 overflow-y-auto p-3 text-xs">
      <p className="panel-title">{TILE_NAMES[tile.type] ?? tile.type}</p>

      {/* Any building blocks wild gathering on its tile. */}
      {wild && !shownBuilding ? (
        <div>
          <div className="flex justify-between text-fg-muted">
            <span>{resourceName}</span>
            <span className="tabular-nums">
              {wild.remaining <= 0
                ? 'depleted'
                : `${Math.ceil(wild.remaining)} / ${Math.ceil(wild.initial)} left`}
            </span>
          </div>
          <Bar
            fraction={wild.initial > 0 ? wild.remaining / wild.initial : 0}
            className={tile.type === 'forest' ? 'bg-ok' : 'bg-warn'}
          />
          {/* No time-until-empty: gatherers leave to haul their loads home. */}
          {ratePerSecond > 0 && wild.remaining > 0 ? (
            <p className="mt-1 text-fg-subtle">
              {gatherers.length} gathering · +{ratePerSecond.toFixed(2)}/s while gathering
            </p>
          ) : null}
        </div>
      ) : null}

      {/* A mine on grass or beach provides metal, shown with the building below. */}
      {!wild && !buildingDef?.provides && (tile.type === 'beach' || tile.type === 'default') ? (
        <p className="text-fg-subtle">No resources</p>
      ) : null}
      {tile.type === 'water' ? <p className="text-fg-subtle">Impassable</p> : null}

      {shownBuilding && buildingDef ? (
        <div>
          <div className="flex justify-between text-fg-muted">
            <span>
              {buildingDef.name}
              {buildingDef.levels ? ` · level ${buildingLevel(shownBuilding)}` : ''}
              {shownBuilding.owner === playerId ? ' (yours)' : ''}
            </span>
            <span className="tabular-nums">
              {Math.ceil(fromMilli(shownBuilding.hp))}/{maxHp} hp
            </span>
          </div>
          {remembered && !intel ? <p className="text-fg-subtle">As last seen</p> : null}
          {intel ? <p className="mt-0.5 text-fg-subtle">{intelNote(intel)}</p> : null}
          {build ? (
            <>
              <Bar fraction={build.progress} className="bg-accent" />
              <p className="mt-1 text-fg-subtle">
                Building {Math.floor(build.progress * 100)}%
                {remembered
                  ? ''
                  : build.secondsLeft === null
                    ? ' · waiting for a worker'
                    : ` · ${formatDuration(build.secondsLeft)} left`}
              </p>
            </>
          ) : idleOutside ? (
            <p className="mt-1 text-warn">Outside your territory: produces nothing</p>
          ) : providesForever && buildingDef.provides ? (
            <p className="mt-1 text-fg-subtle">
              Workers here gather{' '}
              {(rules.resources[buildingDef.provides.resource]?.name ?? '').toLowerCase()} forever
              {ratePerSecond > 0 ? ` · +${ratePerSecond.toFixed(2)}/s` : ''}
              {inSight && !remembered && workers
                ? ` · ${workers.inside + workers.hauling}${
                    // A rival's slots would give away tiles out of sight.
                    ownBuilding ? `/${workerSlots(rules, shownBuilding)}` : ''
                  } workers${workers.hauling > 0 ? ` (${workers.hauling} hauling)` : ''}${
                    ownBuilding && workers.full > 0 ? ` (${workers.full} full)` : ''
                  }`
                : ''}
            </p>
          ) : null}
          {extraTiles > 0 ? <p className="mt-1 text-fg-subtle">{1 + extraTiles} tiles</p> : null}
          {growth ? <p className="mt-1 text-fg-subtle">{growth}</p> : null}
          {strength > 0 && !remembered && shownBuilding.buildProgress >= 1 ? (
            <p className="mt-1 text-fg-subtle">Fires {strength}/s</p>
          ) : null}
        </div>
      ) : null}

      {buildable.map((type) => {
        const preview = {
          id: '',
          owner: playerId,
          type,
          pos: tile.coordinates,
          hp: 0,
          buildProgress: 1,
        };
        const room = roomToGrow(state, ctx, preview);
        return (
          <p key={type} className="text-ok">
            {rules.buildings[type]?.name ?? type} can be built here
            {room ? <span className="text-fg-subtle"> · {room}</span> : null}
          </p>
        );
      })}
    </div>
  );
}

/**
 * The hovered tile's card as a tooltip beside the cursor (the contextual HUD
 * layout). It sits on the cursor's left (the enemy tooltip and the work label
 * take its right), flipping at the window's edges (`labelPosition`, mirrored).
 * It moves itself, so the cursor moving re-renders nothing, and places itself
 * again when its card changes size; hidden until the pointer first moves.
 */
export function CursorTileTip({ ui }: { ui: UiStore }) {
  const tip = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = tip.current;
    if (!el) return;
    let pointer: { x: number; y: number } | null = null;
    const place = () => {
      if (!pointer) return;
      const label = { w: el.offsetWidth, h: el.offsetHeight };
      const view = { w: window.innerWidth, h: window.innerHeight };
      const at = labelPosition({ x: view.w - pointer.x, y: pointer.y }, label, view);
      el.style.transform = `translate(${Math.max(0, view.w - at.x - label.w)}px, ${at.y}px)`;
      el.style.visibility = 'visible';
    };
    const move = (e: PointerEvent) => {
      pointer = { x: e.clientX, y: e.clientY };
      place();
    };
    const resized = new ResizeObserver(place);
    resized.observe(el);
    window.addEventListener('pointermove', move);
    return () => {
      window.removeEventListener('pointermove', move);
      resized.disconnect();
    };
  }, []);
  return (
    <div
      ref={tip}
      className="pointer-events-none invisible fixed left-0 top-0 z-30 flex max-h-[45vh] w-60 flex-col"
    >
      <HoveredTileInfo ui={ui} inspectKey={null} />
    </div>
  );
}
